import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Suspense } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { PageOutro } from "@/components/site/PageEssentials";
import { Section, CTABand, FAQ } from "@/components/site/Blocks";
import { getStateHero } from "@/data/state-heroes";
import { fmrFaqs } from "@/lib/hud-data-faqs";
import { getStateFmr } from "@/lib/fmr.functions";
import { getState } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getMarketStats } from "@/lib/market-stats.functions";

const fmrOptions = (state: string) =>
  queryOptions({
    queryKey: ["stateFmr", state],
    queryFn: () => getStateFmr({ data: { state } }),
    staleTime: 60 * 60 * 1000,
  });

export const Route = createFileRoute("/hud-fair-market-rents/$state")({
  loader: async ({ params, context }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    context.queryClient.ensureQueryData(fmrOptions(state.abbrev));
    const marketStats = await getMarketStats({ data: { stateSlug: state.slug } }).catch(() => null);
    return { state, marketStats };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: "HUD Fair Market Rents by State | Simply Approved Mortgages" }] };
    const url = `/hud-fair-market-rents/${params.state}`;
    const title = `Fair Market Rents in ${state.name} | Simply Approved`;
    const description = `HUD FMR by county in ${state.name}. Studio–4BR Fair Market Rents for Section 8 / HCV underwriting, plus DSCR modeling for ${state.topMarkets.slice(0, 3).join(", ")}.`;
    return buildHead({
      title,
      description,
      url,
      type: "article",
      keywords: `${state.name} HUD fair market rent, ${state.name} Section 8 rent limits, ${state.name} FMR by county, ${state.name} voucher payment standard, ${state.name} HAP contract rent, Section 8 rent ${state.name}, ${state.name} rental income data, HUD FMR ${state.name} 2026, ${state.name} DSCR loan Section 8, ${state.name} affordable housing rent limits`,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "HUD Fair Market Rents", url: "/hud-fair-market-rents" },
        { name: state.name, url },
      ],
      service: { name: "HUD Fair Market Rent Lookup", state: state.name },
      faqs: fmrFaqs(state.name),
      image: getStateHero(state.slug)?.src,
      imageAlt: getStateHero(state.slug)?.alt ?? `HUD data for ${state.name}`,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: `2026 HUD Fair Market Rents — ${state.name}`,
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/pdrdatas_landing.html",
          spatialCoverage: { "@type": "AdministrativeArea", name: state.name },
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: FmrStatePage,
  notFoundComponent: () => (
    <SiteShell>
      <Section>
        <p>State not found. <Link to="/hud-fair-market-rents" className="text-brand underline">See all states</Link>.</p>
      </Section>
    </SiteShell>
  ),
  errorComponent: ({ error }) => (
    <SiteShell>
      <Section>
        <p className="text-destructive">Couldn't load FMR data: {(error as Error).message}</p>
      </Section>
    </SiteShell>
  ),
});

function FmrStatePage() {
  const { state, marketStats } = Route.useLoaderData();

  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD Fair Market Rents"
        title={`2026 HUD FMR in ${state.name}`}
        subtitle={`Every county's 40th-percentile Fair Market Rent for FY2026. Use to underwrite Section 8 HAP contracts and screen high-cash-flow ${state.name} markets.`}
        primaryLabel="Start My Approval"
        primaryHref="/apply"
        secondaryLabel="See Today's Rates"
        secondaryHref="/rates"
      />
      {/* Hero sits outside Suspense so the <h1> ships in the SSR HTML while the
          live HUD tables stream in. */}
      <Suspense fallback={<TableSkeleton />}>
        <FmrData state={state} />
      </Suspense>
      <Section>
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-4 text-center font-display text-2xl font-bold text-foreground">
            {state.name} Fair Market Rent — FAQs
          </h2>
          <FAQ items={fmrFaqs(state.name)} />
        </div>
      </Section>
      <LocalMarketNews
        stateName={state.name}
        stateAbbrev={state.abbrev}
        stats={marketStats ?? null}
        statsScope="state"
      />
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title={`Buying Section 8 property in ${state.name}?`}
        body="Send the address and asking rents — we'll compare to HUD FMR and reply with DSCR pricing feedback."
      />
      <PageOutro />
    </SiteShell>
  );
}

function TableSkeleton() {
  return (
    <Section>
      <div className="mx-auto max-w-5xl animate-pulse space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl border border-border bg-card" />
          ))}
        </div>
        <div className="h-64 rounded-xl border border-border bg-card" />
      </div>
    </Section>
  );
}

function FmrData({ state }: { state: { name: string; abbrev: string; slug: string } }) {
  const { data } = useSuspenseQuery(fmrOptions(state.abbrev));
  const rows = [...(data.entries ?? [])].sort((a, b) => b.rents.threeBr - a.rents.threeBr);
  const top10 = rows.slice(0, 10);
  const bottom10 = rows.slice(-10).reverse();
  const rents3 = rows.map((r) => r.rents.threeBr).filter((v) => v > 0);
  const median = rents3.length ? rents3.slice().sort((a, b) => a - b)[Math.floor(rents3.length / 2)] : 0;
  const max = rents3.length ? Math.max(...rents3) : 0;
  const min = rents3.length ? Math.min(...rents3) : 0;

  return (
    <Section>
      <div className="mx-auto max-w-5xl">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Counties covered" value={rows.length.toLocaleString()} />
          <Stat label="Highest 3BR FMR" value={`$${max.toLocaleString()}`} />
          <Stat label="Median 3BR FMR" value={`$${median.toLocaleString()}`} />
          <Stat label="Lowest 3BR FMR" value={`$${min.toLocaleString()}`} />
        </div>

        <h2 className="mt-12 font-display text-2xl font-bold text-foreground">Top 10 highest-FMR counties in {state.name} (3BR)</h2>
        <FmrTable rows={top10} />

        <h2 className="mt-12 font-display text-2xl font-bold text-foreground">10 lowest-FMR counties in {state.name} (3BR)</h2>
        <FmrTable rows={bottom10} />

        <div className="mt-12 rounded-xl border border-border bg-card p-6">
          <h3 className="font-display text-xl font-semibold text-foreground">What is a Fair Market Rent?</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            HUD's Fair Market Rent (FMR) is the 40th-percentile gross rent for standard-quality units in a Section 8 area.
            PHAs use FMR to set voucher payment standards (usually 90–110% of FMR). Investors use FMR as a ceiling for
            underwriting Section 8 rent in {state.name} because the local housing authority will not approve a HAP contract
            above the rent-reasonableness test tied to FMR.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <Link to="/hud-fair-market-rents" className="rounded-md border border-border px-4 py-2 hover:border-brand">All states →</Link>
          <Link to="/qualified-census-tracts/$state" params={{ state: state.slug }} className="rounded-md border border-border px-4 py-2 hover:border-brand">
            Qualified Census Tracts in {state.name}
          </Link>
          <Link to="/section-8-loans/$state" params={{ state: state.slug }} className="rounded-md bg-brand px-4 py-2 font-semibold text-primary-foreground hover:bg-brand/90">
            Section 8 DSCR loans in {state.name}
          </Link>
        </div>
      </div>
    </Section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-foreground tabular-nums">{value}</div>
    </div>
  );
}

function FmrTable({ rows }: { rows: Array<{ fipsCode: string; countyName: string; rents: { studio: number; oneBr: number; twoBr: number; threeBr: number; fourBr: number } }> }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[560px] text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-2">County / FMR Area</th>
            <th className="px-4 py-2 text-right">Studio</th>
            <th className="px-4 py-2 text-right">1BR</th>
            <th className="px-4 py-2 text-right">2BR</th>
            <th className="px-4 py-2 text-right">3BR</th>
            <th className="px-4 py-2 text-right">4BR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.fipsCode} className="border-t border-border">
              <td className="px-4 py-2 font-medium text-foreground">{r.countyName}</td>
              <td className="px-4 py-2 text-right tabular-nums">${r.rents.studio.toLocaleString()}</td>
              <td className="px-4 py-2 text-right tabular-nums">${r.rents.oneBr.toLocaleString()}</td>
              <td className="px-4 py-2 text-right tabular-nums">${r.rents.twoBr.toLocaleString()}</td>
              <td className="px-4 py-2 text-right font-semibold tabular-nums">${r.rents.threeBr.toLocaleString()}</td>
              <td className="px-4 py-2 text-right tabular-nums">${r.rents.fourBr.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
